import { IErrorType } from '../../constants/types';
import { restoreInitialState } from '../../utils/AppUtils';
import {
    ActivityResponse,
    ActivityType,
    IActivityInitialState,
    IGetFeed,
    IGetFeedResponse,
    TimelinePage,
    types,
    IFeedComment,
    IActivityPagination,
} from './types';
import { types as contestTypes, IBookmark, ICreateBookmark } from '../contest/types';
import produce from 'immer';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { types as authType } from '../authentication/types';
import { types as thougthTypes } from '../thougth/types';
import { types as eventTypes } from '../events/types';
import { types as auditionTypes } from '../audition/types';
import { types as audienceTypes } from '../audience/types';
import { types as projectTypes } from '../project/types';
import { IFollowThspian, IFollowThspianModel } from '../audience/model';
import { generateTemporaryFollowObject } from '../audience/action';

const initialState: IActivityInitialState = {
    myActivities: [],
    timeline: {
        chronological: {
            canLoadMore: false,
            data: [],
            limit: 10,
            page: 0,
            loading: false,
        },
        following: {
            canLoadMore: false,
            data: [],
            limit: 10,
            page: 0,
            loading: false,
        },
        interest: {
            canLoadMore: false,
            data: [],
            limit: 10,
            page: 0,
            loading: false,
        },
    },
    gettingActivity: false,
    savingActivity: false,
    errMsg: null,
    searchSuggestions: [],
    timelinePage: {
        chronological: 0,
        following: 0,
        interest: 0,
    },
    bookmarking: false,
    feedComments: null,
    fetchingFeedComments: false,
    fetchingMoreReplies: false,
    currentComments: [],
    currentCommentsCategory: undefined,
    currentCommentsCategoryId: undefined,
    likingActivity: false,
    deletingActivityLike: false,
    firstCursor: [],
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case types.GET_MY_ACTIVITIES:
            return produce(state, draft => {
                draft.gettingActivity = true;
            });
        case types.GET_MY_ACTIVITIES_SUCCESS:
            return produce(state, draft => {
                draft.gettingActivity = false;
                draft.myActivities = payload; // Parse this response
            });
        case types.GET_MY_ACTIVITIES_FAIL:
            return produce(state, draft => {
                draft.gettingActivity = false;
                draft.errMsg = payload;
            });
        case types.SAVE_ACTIVITY:
            return produce(state, draft => {
                draft.savingActivity = true;
            });
        case types.SAVE_ACTIVITY_SUCCESS:
            return produce(state, draft => {
                draft.savingActivity = false;
            });
        case types.SAVE_ACTIVITY_FAIL:
            return produce(state, draft => {
                draft.savingActivity = false;
            });
        case types.GET_SEARCH_SUGGESTIONS:
            return produce(state, draft => {
                draft.gettingActivity = true;
            });
        case types.GET_SEARCH_SUGGESTIONS_SUCCESS:
            return produce(state, draft => {
                draft.searchSuggestions = payload;
                draft.gettingActivity = false;
            });
        case types.GET_SEARCH_SUGGESTIONS_FAIL:
            return produce(state, draft => {
                draft.errMsg = payload;
                draft.gettingActivity = false;
            });
        case types.HIDE_MY_ACTIVITY:
            return produce(state, draft => {
                draft.processing = true;
            });
        case types.HIDE_MY_ACTIVITY_SUCCESS:
            return produce(state, draft => {
                const { _id } = payload as ActivityResponse;
                const filteredActivity = draft.myActivities.filter(aty => _id !== aty._id);
                draft.myActivities = filteredActivity;
                draft.processing = false;
            });
        case types.HIDE_MY_ACTIVITY_FAIL:
            return produce(state, draft => {
                draft.processing = false;
            });
        case types.GET_ACTIVITY_FEED:
            return produce(state, draft => {
                draft.fetchingTimeline = true;
                const { category, page, cursor, activity_type } = payload as IGetFeed;

                const isFirstPage = !cursor;
                const resetTimelineData = (catName: keyof TimelinePage, page: number) => {
                    return {
                        // data: isFirstPage ? [] : [...draft.timeline[category].data],
                        ...draft.timeline[catName],
                        loading: catName === category,
                        data: isFirstPage ? [] : draft.timeline[catName].data,
                        page,
                        cursor,
                    };
                };

                if (category && draft.timeline) {
                    // Update page number for the category
                    draft.timelinePage[category] = Number(page);
                    draft.timeline[category] = resetTimelineData(category, page!);

                    if (category === 'following') {
                        draft.timeline['interest'] = resetTimelineData('interest', 0!);
                        draft.timeline['chronological'] = resetTimelineData('chronological', 0!);
                    }

                    if (category === 'interest') {
                        draft.timeline['chronological'] = resetTimelineData('chronological', 0!);
                    }
                    if (activity_type && isFirstPage) {
                        draft.timeline['interest'] = resetTimelineData('interest', 0!);
                        draft.timeline['following'] = resetTimelineData('following', 0!);
                    }
                }

                /** Set the pagination to remember what to call next */
                if (page && page < 2 && category === 'chronological') {
                    draft.timeline[category as keyof TimelinePage].currentPagination = payload;
                    draft.fetchingFilters = true;
                }
            });

        case types.GET_ACTIVITY_FEED_SUCCESS:
            return produce(state, draft => {
                draft.fetchingTimeline = false;
                const { pagination, filters } = payload as IGetFeedResponse;
                const { category, page, limit, hasMoreByCategory, cursors, cursor } = pagination as IActivityPagination;
                if (category) {
                    /** Add the pagination */
                    draft.timeline[category].data = [...draft.timeline[category].data, ...payload[category]];
                }
                draft.timeline[category!].canLoadMore =
                    hasMoreByCategory && category ? hasMoreByCategory[category] : true;
                draft.timeline[category!].nextCursor =
                    hasMoreByCategory && cursors && category ? cursors[category] : null;
                draft.timeline[category!].limit = Number(limit);
                draft.timeline[category!].loading = false;
                if (cursor) {
                    if (draft.firstCursor?.length < 1) {
                        draft.firstCursor.push(cursor);
                    }
                    draft.nextCursor = cursor;
                }
                Object.keys(draft.timeline).forEach(cat => {
                    draft.timeline[cat as keyof TimelinePage].loading = false;
                });
                draft.timeline[category!].page = Number(page);
                draft.timelinePage[category as keyof TimelinePage] = Number(page);
                if (category === 'following' && !hasMoreByCategory[category]) {
                    draft.timeline['interest'].canLoadMore = true;
                }

                if (category === 'interest' && !hasMoreByCategory[category]) {
                    draft.timeline['chronological'].canLoadMore = true;
                }
                // This should always be true
                if (filters && Number(page) < 2) {
                    draft.filters = filters;
                    draft.fetchingFilters = false;
                }
            });
        case types.GET_ACTIVITY_FEED_FAIL:
            return produce(state, draft => {
                const { additionalPayload } = payload as IErrorType;
                const { category } = additionalPayload;
                draft.timeline[category! as keyof TimelinePage].loading = false;
                draft.fetchingTimeline = false;
                draft.errMsg = payload;
                draft.fetchingFilters = false;
                Object.keys(draft.timeline).forEach(cat => {
                    draft.timeline[cat! as keyof TimelinePage].loading = false;
                });
            });
        case types.GET_FEED_SUGGESTION:
            return produce(state, draft => {
                draft.fetchingSuggestion = true;
            });
        case types.GET_FEED_SUGGESTION_SUCCESS:
            return produce(state, draft => {
                draft.fetchingSuggestion = false;
                draft.feedSuggestion = payload;
            });
        case types.GET_FEED_SUGGESTION_FAIL:
            return produce(state, draft => {
                draft.fetchingSuggestion = false;
                draft.errMsg = payload;
            });
        case contestTypes.CREATE_BOOKMARK:
            return produce(state, draft => {
                draft.bookmarking = true;
                const {
                    details: { model, type },
                } = payload as ICreateBookmark;
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(item => {
                        if (type === 'Audition' && item && item.auditions_activity?._id === model) {
                            // Found the audition activity, update has_user_bookmarked
                            if (!item.auditions_activity.user_bookmark) {
                                item.auditions_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.auditions_activity.has_user_bookmarked = true;
                            item.auditions_activity.user_bookmark.push('optimistic');
                        } else if (type === 'Event' && item && item.events_activity?._id === model) {
                            // Found the event activity, update has_user_bookmarked
                            if (!item.events_activity.user_bookmark) {
                                item.events_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.events_activity.has_user_bookmarked = true;
                            item.events_activity.user_bookmark.push('optimistic');
                        }
                        // Add similar conditions for contests_activity, projects_activity, etc., if needed
                    });
                });
            });
        case contestTypes.CREATE_BOOKMARK_SUCCESS:
            return produce(state, draft => {
                draft.bookmarking = false;
                const { details, _id } = payload as IBookmark;
                const { model, type } = details;

                // Override the optimistic bookmarks
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(item => {
                        if (
                            type === 'Audition' &&
                            item &&
                            item.auditions_activity &&
                            item.auditions_activity?._id === model._id
                        ) {
                            if (!item.auditions_activity?.user_bookmark) {
                                item.auditions_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.auditions_activity['user_bookmark'] = [_id];
                        } else if (
                            type === 'Event' &&
                            item &&
                            item.auditions_activity &&
                            item.events_activity?._id === model._id
                        ) {
                            if (!item.events_activity.user_bookmark) {
                                item.events_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.events_activity['user_bookmark'] = [_id];
                        }
                    });
                });
            });
        case contestTypes.CREATE_BOOKMARK_FAIL:
            return produce(state, draft => {
                draft.bookmarking = false;
                const { additionalPayload } = payload as IErrorType;
                const {
                    details: { model, type },
                } = additionalPayload as ICreateBookmark;
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(item => {
                        if (type === 'Audition' && item && item.auditions_activity?._id === model) {
                            item.auditions_activity.has_user_bookmarked = false;
                            if (!item.auditions_activity.user_bookmark) {
                                item.auditions_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.auditions_activity['user_bookmark'] = [];
                        } else if (type === 'Event' && item && item.events_activity?._id === model) {
                            item.events_activity.has_user_bookmarked = false;
                            if (!item.events_activity.user_bookmark) {
                                item.events_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.events_activity['user_bookmark'] = [];
                        }
                    });
                });
            });
        case contestTypes.DELETE_BOOKMARK:
            return produce(state, draft => {
                draft.bookmarking = true;
                const { morePayload, additionalPayload } = payload as IGenericDocumentModifier;
                if (additionalPayload && morePayload) {
                    const { _id } = additionalPayload;
                    // Search through all timelines (chronological, following, interest)
                    ['chronological', 'following', 'interest'].forEach(category => {
                        draft.timeline[category as keyof TimelinePage].data.forEach(item => {
                            if (
                                morePayload === 'Audition' &&
                                item &&
                                item.auditions_activity?._id === (_id as string)
                            ) {
                                // Found the audition activity, update has_user_bookmarked
                                item.auditions_activity.has_user_bookmarked = false;
                                item.auditions_activity['user_bookmark'] = [];
                            } else if (
                                morePayload === 'Event' &&
                                item &&
                                item.events_activity?._id === (_id as string)
                            ) {
                                // Found the event activity, update has_user_bookmarked
                                item.events_activity.has_user_bookmarked = false;
                                item.events_activity['user_bookmark'] = [];
                            }
                            // Add similar conditions for contests_activity, projects_activity, etc., if needed
                        });
                    });
                }
            });
        case contestTypes.DELETE_BOOKMARK_SUCCESS:
            return produce(state, draft => {
                draft.bookmarking = false;
                /** Do nothing as the delete is successfull */
            });
        case contestTypes.DELETE_BOOKMARK_FAIL:
            return produce(state, draft => {
                draft.bookmarking = false;
                const { additionalPayload, documentId } = payload as IErrorType;
                const { morePayload } = additionalPayload as IGenericDocumentModifier;
                // Override the optimistic bookmarks
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(item => {
                        if (
                            item &&
                            morePayload === 'Audition' &&
                            item.auditions_activity?._id === (documentId as string)
                        ) {
                            if (!item.auditions_activity.user_bookmark) {
                                item.auditions_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.auditions_activity['user_bookmark'] = [documentId as string];
                        } else if (
                            morePayload === 'Event' &&
                            item &&
                            item.events_activity?._id === (documentId as string)
                        ) {
                            if (!item.events_activity.user_bookmark) {
                                item.events_activity.user_bookmark = []; // Initialize if undefined
                            }
                            item.events_activity['user_bookmark'] = [documentId as string];
                        }
                    });
                });
            });
        case thougthTypes.PUBLISH_THOUGTH_SUCCESS:
            const thougthActivityPayload = {
                _id: `id-${Date.now().toString()}`,
                user: payload.created_by._id,
                activity_type: 'created a thougth' as ActivityType,
                activity: {
                    type: 'Thougth',
                    model: payload,
                },
                activity_collection: 'thougths',
                created_by: payload.created_by,
                status: 'published',
                createdAt: payload.createdAt,
                updatedAt: payload.updatedAt,
                id: `id-${Date.now().toString()}`,
                thougths_activity: payload,
            };
            return produce(state, draft => {
                draft.timeline.following = {
                    ...draft.timeline.following,
                    data: [thougthActivityPayload, ...draft.timeline.following.data],
                };
            });
        case eventTypes.PUBLISH_EVENT_SUCCESS:
            const eventActivityPayload = {
                _id: `id-${Date.now().toString()}`,
                user: payload.created_by._id,
                activity_type: 'created an event' as ActivityType,
                activity: {
                    type: 'Event',
                    model: payload,
                },
                activity_collection: 'events',
                created_by: payload.created_by,
                status: 'published',
                createdAt: payload.createdAt,
                updatedAt: payload.updatedAt,
                id: `id-${Date.now().toString()}`,
                events_activity: payload,
            };
            return produce(state, draft => {
                draft.timeline.following = {
                    ...draft.timeline.following,
                    data: [eventActivityPayload, ...draft.timeline.following.data],
                };
            });
        case auditionTypes.PUBLISH_AUDITION_SUCCESS:
            const aaudienceAtivityPayload = {
                _id: `id-${Date.now().toString()}`,
                user: payload.created_by._id,
                activity_type: 'is hiring' as ActivityType,
                activity: {
                    type: 'Audition',
                    model: payload,
                },
                activity_collection: 'auditions',
                created_by: payload.created_by,
                status: 'published',
                createdAt: payload.createdAt,
                updatedAt: payload.updatedAt,
                id: `id-${Date.now().toString()}`,
                auditions_activity: payload,
            };
            return produce(state, draft => {
                draft.timeline.following = {
                    ...draft.timeline.following,
                    data: [aaudienceAtivityPayload, ...draft.timeline.following.data],
                };
            });
        case audienceTypes.FOLLOW_A_THSPIAN:
            return produce(state, draft => {
                const { following, from } = payload as IFollowThspian;
                if (from === 'feed') {
                    // Update all timeline categories (chronological, following, interest)
                    ['chronological', 'following', 'interest'].forEach(category => {
                        draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                            if (activity.created_by._id === following) {
                                activity.created_by.user_following = true;
                                if (!activity.created_by.user_follow_interaction) {
                                    activity.created_by.user_follow_interaction = [];
                                }
                                const temporaryFollowObject = generateTemporaryFollowObject(following);
                                activity.created_by.user_follow_interaction.push(temporaryFollowObject);
                            }
                        });
                    });
                }
            });
        case audienceTypes.FOLLOW_A_THSPIAN_SUCCESS:
            return produce(state, draft => {
                const { following, from } = payload as IFollowThspianModel;
                if (from === 'feed') {
                    // Update all timeline categories
                    ['chronological', 'following', 'interest'].forEach(category => {
                        draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                            if (activity.created_by._id === following._id) {
                                // Replace optimistic follow object with real one
                                const followIndex = activity.created_by.user_follow_interaction?.findIndex(
                                    follow => follow.optimistic && follow.following._id === following._id,
                                );
                                if (
                                    followIndex !== undefined &&
                                    followIndex > -1 &&
                                    activity.created_by.user_follow_interaction
                                ) {
                                    activity.created_by.user_follow_interaction[followIndex] = payload;
                                }
                            }
                        });
                    });
                }
            });
        case audienceTypes.FOLLOW_A_THSPIAN_FAIL:
            return produce(state, draft => {
                const { documentId } = payload as IErrorType;
                // Revert optimistic changes on failure
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                        if (activity.created_by._id === documentId) {
                            activity.created_by.user_following = false;
                            if (activity.created_by.user_follow_interaction) {
                                activity.created_by.user_follow_interaction =
                                    activity.created_by.user_follow_interaction.filter(
                                        follow => !follow.optimistic || follow.following._id !== documentId,
                                    );
                            }
                        }
                    });
                });
            });

        case audienceTypes.UNFOLLOW_A_THSPIAN:
            return produce(state, draft => {
                const { additionalPayload } = payload as IGenericDocumentModifier;
                // Optimistically remove follow status
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                        if (activity.created_by._id === additionalPayload.following._id) {
                            activity.created_by.user_following = false;
                            if (activity.created_by.user_follow_interaction) {
                                activity.created_by.user_follow_interaction = [];
                            }
                        }
                    });
                });
            });
        case audienceTypes.UNFOLLOW_A_THSPIAN_SUCCESS:
            return produce(state, draft => {
                // Success - no additional changes needed as optimistic update was correct
            });
        case audienceTypes.UNFOLLOW_A_THSPIAN_FAIL:
            return produce(state, draft => {
                const { additionalPayload } = payload as IGenericDocumentModifier;
                // Revert optimistic changes on failure
                ['chronological', 'following', 'interest'].forEach(category => {
                    draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                        if (activity.created_by._id === additionalPayload.following._id) {
                            activity.created_by.user_following = true;
                            if (!activity.created_by.user_follow_interaction) {
                                activity.created_by.user_follow_interaction = [];
                            }
                            activity.created_by.user_follow_interaction.push(additionalPayload);
                        }
                    });
                });
            });
        case types.GET_FEED_COMMENT:
            return produce(state, draft => {
                const { category, categoryId, page, parent_comment } = payload;
                draft.fetchingFeedComments = true;

                if (parent_comment) draft.fetchingMoreReplies = true;

                // Clear comments if it's a new category/categoryId or page 1 (new fetch)
                if (
                    draft.currentCommentsCategory !== category ||
                    draft.currentCommentsCategoryId !== categoryId ||
                    page === 1
                ) {
                    draft.currentComments = [];
                    draft.currentCommentsCategory = category;
                    draft.currentCommentsCategoryId = categoryId;
                }
            });
        case types.GET_FEED_COMMENT_SUCCESS:
            return produce(state, draft => {
                const { pagination, data, parent_comment } = payload;
                draft.fetchingFeedComments = false;
                draft.fetchingMoreReplies = false;

                if (parent_comment) {
                    // Loading replies for a specific comment
                    const updateReplies = (comment: IFeedComment): boolean => {
                        if (comment._id === parent_comment) {
                            // Append new replies to first_replies
                            if (data && data.length > 0) {
                                comment.first_replies = [...(comment.first_replies || []), ...data];
                            }
                            return true;
                        }

                        // Check nested replies
                        if (comment.first_replies) {
                            for (const reply of comment.first_replies) {
                                if (updateReplies(reply)) {
                                    return true;
                                }
                            }
                        }

                        return false;
                    };

                    // Find and update the parent comment with new replies
                    draft.currentComments.forEach(comment => {
                        updateReplies(comment);
                        if (comment._id === parent_comment) {
                            comment.pagination = {
                                ...comment.pagination,
                                hasNextPage: pagination.hasNextPage,
                                currentPage: pagination.page,
                                limit: pagination.limit,
                                nextPage: pagination.page + 1,
                            };
                        }
                    });
                } else {
                    // Loading main comments
                    draft.feedComments = payload;

                    // If it's page 1, replace comments; otherwise append
                    if (pagination.page === 1) {
                        draft.currentComments = data || [];
                    } else {
                        // For pagination, append new comments
                        draft.currentComments = [...draft.currentComments, ...(data || [])];
                    }
                }
            });
        case types.GET_FEED_COMMENT_FAIL:
            return produce(state, draft => {
                draft.fetchingFeedComments = false;
                draft.fetchingMoreReplies = false;
                draft.errMsg = payload;
            });
        case projectTypes.LEAVE_COMMENT_SUCCESS:
            return produce(state, draft => {
                const { comment, isReply, parentComment, commentOrigin } = payload;
                if (commentOrigin === 'feed' && comment) {
                    // Add the new comment to currentComments if it's for the current category
                    const commentCategoryId = comment.thougth || comment.project || comment.contest || comment.act;
                    if (draft.currentCommentsCategoryId === commentCategoryId) {
                        if (isReply && parentComment) {
                            // Recursive function to find and add reply to deeply nested comments
                            const addReplyToComment = (targetComment: IFeedComment): boolean => {
                                if (targetComment._id === parentComment._id) {
                                    // Initialize first_replies if it doesn't exist
                                    if (!targetComment.first_replies) {
                                        targetComment.first_replies = [];
                                    }
                                    targetComment.first_replies.unshift(comment as any);
                                    targetComment.total_replies = (targetComment.total_replies || 0) + 1;
                                    return true; // Found and added
                                }

                                // Check first-level replies
                                if (targetComment.first_replies) {
                                    for (const reply of targetComment.first_replies) {
                                        if (addReplyToComment(reply)) {
                                            return true; // Found in nested replies
                                        }
                                    }
                                }

                                // Check regular replies array if it exists
                                if (targetComment.replies) {
                                    for (const reply of targetComment.replies) {
                                        if (addReplyToComment(reply)) {
                                            return true; // Found in nested replies
                                        }
                                    }
                                }

                                return false; // Not found in this branch
                            };

                            // Try to add the reply to the appropriate parent comment
                            let replyAdded = false;
                            for (const topLevelComment of draft.currentComments) {
                                if (addReplyToComment(topLevelComment)) {
                                    replyAdded = true;
                                    break;
                                }
                            }

                            // If parent not found in current comments, add as a top-level comment
                            if (!replyAdded) {
                                draft.currentComments.unshift(comment as any);
                            }
                        } else {
                            // Add new main comment at the beginning
                            draft.currentComments.unshift(comment as any);
                        }
                        if (draft.feedComments?.pagination) {
                            const currentTotal = draft.feedComments.pagination.total ?? 0;

                            draft.feedComments = {
                                ...draft.feedComments,
                                pagination: {
                                    ...draft.feedComments.pagination,
                                    total: currentTotal + 1,
                                },
                            };
                        }
                    }

                    // Update comment count in activity feed items
                    ['chronological', 'following', 'interest'].forEach(category => {
                        draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                            if (
                                activity.thougths_activity?._id === comment.thougth ||
                                activity.projects_activity?._id === comment.project ||
                                activity.contests_activity?._id === comment.contest
                            ) {
                                // Increment comment count
                                if (activity.thougths_activity && comment.thougth) {
                                    activity.thougths_activity.total_comments =
                                        (activity.thougths_activity.total_comments || 0) + 1;
                                }
                                if (activity.projects_activity && comment.project) {
                                    activity.projects_activity.total_comments =
                                        (activity.projects_activity.total_comments || 0) + 1;
                                }
                                if (activity.contests_activity && comment.contest) {
                                    activity.contests_activity.total_comments =
                                        (activity.contests_activity.total_comments || 0) + 1;
                                }
                            }
                        });
                    });
                }
            });
        case projectTypes.LIKE_COMMENT:
            return produce(state, draft => {
                const { commentId } = payload;

                // Recursive function to optimistically like comment
                const optimisticallyLikeComment = (comment: any): boolean => {
                    if (comment._id === commentId) {
                        comment.is_user_liked = true;
                        comment.total_likes = (comment.total_likes || 0) + 1;
                        return true; // Found and updated
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (optimisticallyLikeComment(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (optimisticallyLikeComment(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Optimistically update like status in current comments
                draft.currentComments.forEach(comment => {
                    optimisticallyLikeComment(comment);
                });
            });
        case projectTypes.LIKE_COMMENT_SUCCESS:
            return produce(state, draft => {
                const { data, documentId } = payload;

                // Recursive function to confirm like with server data
                const confirmCommentLike = (comment: any): boolean => {
                    if (comment._id === documentId) {
                        comment.is_user_liked = true;
                        comment.user_like = [data]; // Add server response data
                        // Don't increment total_likes again as it was already done optimistically
                        return true; // Found and updated
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (confirmCommentLike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (confirmCommentLike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Confirm like status with server data
                draft.currentComments.forEach(comment => {
                    confirmCommentLike(comment);
                });
            });
        case projectTypes.LIKE_COMMENT_FAIL:
            return produce(state, draft => {
                const { documentId } = payload;

                // Recursive function to revert optimistic like on failure
                const revertCommentLike = (comment: any): boolean => {
                    if (comment._id === documentId) {
                        comment.is_user_liked = false;
                        comment.total_likes = Math.max((comment.total_likes || 0) - 1, 0);
                        comment.user_like = [];
                        return true; // Found and reverted
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (revertCommentLike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (revertCommentLike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Revert optimistic like on failure
                draft.currentComments.forEach(comment => {
                    revertCommentLike(comment);
                });
            });
        case projectTypes.DELETE_COMMENT_LIKE:
            return produce(state, draft => {
                const { additionalPayload } = payload;
                const commentId = additionalPayload._id;

                // Recursive function to optimistically unlike comment
                const optimisticallyUnlikeComment = (comment: any): boolean => {
                    if (comment._id === commentId) {
                        comment.is_user_liked = false;
                        comment.total_likes = Math.max((comment.total_likes || 0) - 1, 0);
                        return true; // Found and updated
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (optimisticallyUnlikeComment(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (optimisticallyUnlikeComment(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Optimistically update unlike status in current comments
                draft.currentComments.forEach(comment => {
                    optimisticallyUnlikeComment(comment);
                });
                if (draft.feedComments?.pagination) {
                    const currentTotal = draft.feedComments.pagination.total ?? 0;

                    draft.feedComments = {
                        ...draft.feedComments,
                        pagination: {
                            ...draft.feedComments.pagination,
                            total: currentTotal === 1 ? 0 : currentTotal - 1,
                        },
                    };
                }
            });
        case projectTypes.DELETE_COMMENT_LIKE_SUCCESS:
            return produce(state, draft => {
                const { additionalPayload } = payload;
                const commentId = additionalPayload._id;

                // Recursive function to confirm unlike with server data
                const confirmCommentUnlike = (comment: any): boolean => {
                    if (comment._id === commentId) {
                        comment.is_user_liked = false;
                        comment.user_like = []; // Clear user like data
                        // Don't decrement total_likes again as it was already done optimistically
                        return true; // Found and updated
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (confirmCommentUnlike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (confirmCommentUnlike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Confirm unlike status with server data
                draft.currentComments.forEach(comment => {
                    confirmCommentUnlike(comment);
                });
            });
        case projectTypes.DELETE_COMMENT_LIKE_FAIL:
            return produce(state, draft => {
                const { documentId } = payload;

                // Recursive function to revert optimistic unlike on failure
                const revertCommentUnlike = (comment: any): boolean => {
                    if (comment._id === documentId) {
                        comment.is_user_liked = true;
                        comment.total_likes = (comment.total_likes || 0) + 1;
                        // Note: We don't have the original user_like data to restore, but that's ok
                        // The UI will still show the correct liked state
                        return true; // Found and reverted
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (revertCommentUnlike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (revertCommentUnlike(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Revert optimistic unlike on failure
                draft.currentComments.forEach(comment => {
                    revertCommentUnlike(comment);
                });
            });
        case projectTypes.UPDATE_COMMENT_SUCCESS:
            return produce(state, draft => {
                const { documentId, additionalPayload } = payload;

                // Recursive function to update comment text in deeply nested comments
                const updateCommentText = (comment: any): boolean => {
                    if (comment._id === documentId) {
                        // Update the comment with the new data from additionalPayload
                        comment.comment = additionalPayload.comment;
                        // Optionally update other fields like updatedAt if available
                        if (additionalPayload.updatedAt) {
                            comment.updatedAt = additionalPayload.updatedAt;
                        }
                        return true; // Found and updated
                    }

                    // Check first-level replies
                    if (comment.first_replies) {
                        for (const reply of comment.first_replies) {
                            if (updateCommentText(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    // Check regular replies array if it exists
                    if (comment.replies) {
                        for (const reply of comment.replies) {
                            if (updateCommentText(reply)) {
                                return true; // Found in nested replies
                            }
                        }
                    }

                    return false; // Not found in this branch
                };

                // Update comment text in current comments (with deep nesting support)
                draft.currentComments.forEach(comment => {
                    updateCommentText(comment);
                });
            });
        case projectTypes.DELETE_COMMENT_SUCCESS:
            return produce(state, draft => {
                const { documentId } = payload;
                const deletedComment = payload.payload; // The actual comment data

                // Recursive function to remove comment from deeply nested comments
                const removeComment = (comments: any[], parentComment?: any): boolean => {
                    for (let i = 0; i < comments.length; i++) {
                        const comment = comments[i];

                        if (comment._id === documentId) {
                            // Remove the comment from the array
                            comments.splice(i, 1);

                            // Update parent's total_replies count if this is a reply
                            if (parentComment) {
                                parentComment.total_replies = Math.max((parentComment.total_replies || 0) - 1, 0);
                            }

                            return true; // Found and removed
                        }

                        // Check first-level replies
                        if (comment.first_replies && removeComment(comment.first_replies, comment)) {
                            return true; // Found and removed from nested replies
                        }

                        // Check regular replies array if it exists
                        if (comment.replies && removeComment(comment.replies, comment)) {
                            return true; // Found and removed from nested replies
                        }
                    }

                    return false; // Not found in this array
                };

                // Remove comment from current comments (with deep nesting support)
                removeComment(draft.currentComments);

                // Update comment count in activity feed items only for matching activities
                if (deletedComment) {
                    ['chronological', 'following', 'interest'].forEach(category => {
                        draft.timeline[category as keyof TimelinePage].data.forEach(activity => {
                            // Only update comment count for activities that match the deleted comment's category
                            if (
                                (deletedComment.thougth &&
                                    activity.thougths_activity?._id === deletedComment.thougth) ||
                                (deletedComment.project &&
                                    activity.projects_activity?._id === deletedComment.project) ||
                                (deletedComment.contest && activity.contests_activity?._id === deletedComment.contest)
                            ) {
                                if (activity.thougths_activity && deletedComment.thougth) {
                                    activity.thougths_activity.total_comments = Math.max(
                                        (activity.thougths_activity.total_comments || 0) - 1,
                                        0,
                                    );
                                }
                                if (activity.projects_activity && deletedComment.project) {
                                    activity.projects_activity.total_comments = Math.max(
                                        (activity.projects_activity.total_comments || 0) - 1,
                                        0,
                                    );
                                }
                                if (activity.contests_activity && deletedComment.contest) {
                                    activity.contests_activity.total_comments = Math.max(
                                        (activity.contests_activity.total_comments || 0) - 1,
                                        0,
                                    );
                                }
                            }
                        });
                    });
                }
            });

        // Activity Like Actions - Optimistic Updates
        case types.LIKE_ACTIVITY:
            return produce(state, draft => {
                const { activityId, like_category } = payload;
                draft.likingActivity = true;

                // Find and optimistically update the activity in timeline
                Object.keys(draft.timeline).forEach(timelineKey => {
                    const timelineData = draft.timeline[timelineKey as keyof typeof draft.timeline];
                    if (timelineData && timelineData.data) {
                        timelineData.data.forEach((item: any) => {
                            // Check different activity types based on like_category
                            let activityToUpdate = null;

                            if (like_category === 'project' && item.projects_activity) {
                                activityToUpdate = item.projects_activity;
                            } else if (like_category === 'contest' && item.contests_activity) {
                                activityToUpdate = item.contests_activity;
                            } else if (like_category === 'thougth' && item.thougths_activity) {
                                activityToUpdate = item.thougths_activity;
                            } else if (like_category === 'event' && item.events_activity) {
                                activityToUpdate = item.events_activity;
                            }

                            if (activityToUpdate && activityToUpdate._id === activityId) {
                                // Optimistic update
                                activityToUpdate.is_user_liked = true;
                                activityToUpdate.total_likes = (activityToUpdate.total_likes || 0) + 1;
                            }
                        });
                    }
                });
            });

        case types.LIKE_ACTIVITY_SUCCESS:
        case projectTypes.LIKE_A_PROJECT_SUCCESS:
            return produce(state, draft => {
                const { data } = payload;
                draft.likingActivity = false;

                // Add the like data to the activity
                if (data && data.like_category) {
                    const activityId = data.project
                        ? data.project._id || data.project
                        : data.contest
                        ? data.contest._id || data.contest
                        : data.thougth
                        ? data.thougth._id || data.thougth
                        : data.event
                        ? data.event._id || data.event
                        : null;

                    if (activityId) {
                        Object.keys(draft.timeline).forEach(timelineKey => {
                            const timelineData = draft.timeline[timelineKey as keyof typeof draft.timeline];
                            if (timelineData && timelineData.data) {
                                timelineData.data.forEach((item: any) => {
                                    // Check different activity types based on like_category
                                    let activityToUpdate = null;

                                    if (data.like_category === 'project' && item.projects_activity) {
                                        activityToUpdate = item.projects_activity;
                                    } else if (data.like_category === 'contest' && item.contests_activity) {
                                        activityToUpdate = item.contests_activity;
                                    } else if (data.like_category === 'thougth' && item.thougths_activity) {
                                        activityToUpdate = item.thougths_activity;
                                    } else if (data.like_category === 'event' && item.events_activity) {
                                        activityToUpdate = item.events_activity;
                                    }

                                    if (activityToUpdate && activityToUpdate._id === activityId) {
                                        // Explicitly update the activity properties
                                        activityToUpdate.is_user_liked = true;
                                        activityToUpdate.user_like = [data];
                                    }
                                });
                            }
                        });
                    }
                }
            });

        case types.LIKE_ACTIVITY_FAIL:
            return produce(state, draft => {
                const { activityId, like_category } = payload;
                draft.likingActivity = false;

                // Revert optimistic update
                Object.keys(draft.timeline).forEach(timelineKey => {
                    const timelineData = draft.timeline[timelineKey as keyof typeof draft.timeline];
                    if (timelineData && timelineData.data) {
                        timelineData.data.forEach((item: any) => {
                            const activityToUpdate =
                                like_category === 'project'
                                    ? item.projects_activity
                                    : like_category === 'contest'
                                    ? item.contests_activity
                                    : like_category === 'thougth'
                                    ? item.thougths_activity
                                    : like_category === 'event'
                                    ? item.events_activity
                                    : null;

                            if (activityToUpdate && activityToUpdate._id === activityId) {
                                // Revert optimistic update
                                activityToUpdate.is_user_liked = false;
                                activityToUpdate.total_likes = Math.max((activityToUpdate.total_likes || 0) - 1, 0);
                                activityToUpdate.user_like = [];
                            }
                        });
                    }
                });
            });

        // Delete Activity Like Actions - Optimistic Updates
        case types.DELETE_ACTIVITY_LIKE:
            return produce(state, draft => {
                const { activityId, like_category } = payload;
                draft.deletingActivityLike = true;

                // Find and optimistically update the activity in timeline
                Object.keys(draft.timeline).forEach(timelineKey => {
                    const timelineData = draft.timeline[timelineKey as keyof typeof draft.timeline];
                    if (timelineData && timelineData.data) {
                        timelineData.data.forEach((item: any) => {
                            const activityToUpdate =
                                like_category === 'project'
                                    ? item.projects_activity
                                    : like_category === 'contest'
                                    ? item.contests_activity
                                    : like_category === 'thougth'
                                    ? item.thougths_activity
                                    : like_category === 'event'
                                    ? item.events_activity
                                    : null;

                            if (activityToUpdate && activityToUpdate._id === activityId) {
                                // Optimistic unlike
                                activityToUpdate.is_user_liked = false;
                                activityToUpdate.total_likes = Math.max((activityToUpdate.total_likes || 0) - 1, 0);
                                activityToUpdate.user_like = [];
                            }
                        });
                    }
                });
            });

        case types.DELETE_ACTIVITY_LIKE_SUCCESS:
            return produce(state, draft => {
                draft.deletingActivityLike = false;
                // The optimistic update is already applied, just confirm it
                // No additional action needed as the unlike was already processed optimistically
            });

        case types.DELETE_ACTIVITY_LIKE_FAIL:
            return produce(state, draft => {
                const { activityId, like_category, additionalPayload } = payload;
                draft.deletingActivityLike = false;

                // Revert optimistic unlike
                Object.keys(draft.timeline).forEach(timelineKey => {
                    const timelineData = draft.timeline[timelineKey as keyof typeof draft.timeline];
                    if (timelineData && timelineData.data) {
                        timelineData.data.forEach((item: any) => {
                            const activityToUpdate =
                                like_category === 'project'
                                    ? item.projects_activity
                                    : like_category === 'contest'
                                    ? item.contests_activity
                                    : like_category === 'thougth'
                                    ? item.thougths_activity
                                    : like_category === 'event'
                                    ? item.events_activity
                                    : null;

                            if (activityToUpdate && activityToUpdate._id === activityId) {
                                // Revert unlike - restore like state
                                activityToUpdate.is_user_liked = true;
                                activityToUpdate.total_likes = (activityToUpdate.total_likes || 0) + 1;
                                if (additionalPayload && additionalPayload.user_like) {
                                    activityToUpdate.user_like = additionalPayload.user_like;
                                }
                            }
                        });
                    }
                });
            });
        case types.GET_PERSISTENT_FEED:
            return produce(state, draft => {
                draft.fetchingTimeline = true;
                draft.timeline.chronological.loading = true;
                draft.timeline.following.loading = true;
                draft.timeline.interest.loading = true;
                draft.fetchingFilters = true;
            });
        case types.GET_PERSISTENT_FEED_SUCCESS:
            return produce(state, draft => {
                draft.fetchingTimeline = false;
                const { filters, chronological, following, interest } = payload;
                if (chronological) {
                    draft.timeline.chronological.data = [...draft.timeline.chronological.data, ...chronological];
                }
                if (following) {
                    draft.timeline.following.data = [...draft.timeline.following.data, ...following];
                }
                if (interest) {
                    draft.timeline.interest.data = [...draft.timeline.interest.data, ...interest];
                }
                draft.timeline.following.loading = false;
                draft.timeline.chronological.loading = false;
                draft.timeline.interest.loading = false;
                draft.fetchingFilters = false;
                if (filters) {
                    draft.filters = filters;
                }
            });
        case types.GET_PERSISTENT_FEED_FAIL:
            return produce(state, draft => {
                draft.fetchingTimeline = false;
                draft.timeline.chronological.loading = false;
                draft.timeline.following.loading = false;
                draft.timeline.interest.loading = false;
                draft.fetchingFilters = false;
            });
        case types.RESET_ACTIVITY_DATA:
        case authType.LOGOUT_SUCCESS:
            return restoreInitialState(state, initialState);
        default:
            return state;
    }
};

export { reducer };
